// client/src/components/billingExecution/BillingExecutionsList.js
import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Button,
  Box,
  CircularProgress,
  Alert,
  Chip,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Assessment as ReportIcon
} from '@mui/icons-material';
import { AuthContext } from '../../contexts/AuthContext';
import { LocaleContext } from '../../contexts/LocaleContext';
import billingReceiptService from '../../services/billingReceiptService';

const BillingExecutionsList = () => {
  const { t, i18n } = useTranslation();
  const { user, isAdmin } = useContext(AuthContext);
  const { direction } = useContext(LocaleContext);
  
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [receiptToDelete, setReceiptToDelete] = useState(null);
  
  // Current language
  const lang = i18n.language;
  
  // Load receipts on component mount
  useEffect(() => {
    loadReceipts();
  }, []);
  
  // Function to load receipts
  const loadReceipts = async () => {
    try {
      setLoading(true);
      const response = await billingReceiptService.getBillingReceipts();
      setReceipts(response.data);
      setError('');
    } catch (err) {
      setError(t('errorLoadingReceipts'));
    } finally {
      setLoading(false);
    }
  };
  
  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return t('notSpecified');
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };
  
  // Get status chip color
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };
  
  // Handle delete click
  const handleDeleteClick = (receipt) => {
    setReceiptToDelete(receipt);
    setDeleteDialogOpen(true);
  };
  
  // Handle delete confirm
  const handleDeleteConfirm = async () => {
    try {
      await billingReceiptService.deleteBillingReceipt(receiptToDelete.id);
      setSuccess(t('receiptDeletedSuccess'));
      loadReceipts();
    } catch (err) {
      setError(t('errorDeletingReceipt'));
    } finally {
      setDeleteDialogOpen(false);
      setReceiptToDelete(null);
    }
  };
  
  // Handle delete cancel
  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setReceiptToDelete(null);
  };
  
  // Handle download Excel
  const handleDownloadExcel = async (id) => {
    try {
      await billingReceiptService.downloadReceiptExcel(id);
    } catch (err) {
      setError(t('errorDownloadingExcel'));
    }
  };
  
  // Handle download distribution Excel
  const handleDownloadDistributionExcel = async (id) => {
    try {
      await billingReceiptService.downloadReceiptDistributionExcel(id);
    } catch (err) {
      setError(t('errorDownloadingDistributionExcel'));
    }
  };
  
  // Handle close alert
  const handleCloseAlert = () => {
    setSuccess('');
    setError('');
  };
  
  // Count items in a receipt
  const countItems = (receipt) => {
    return receipt.items?.length || 0;
  };
  
  // Calculate total value for a receipt
  const calculateTotal = (receipt) => {
    if (!receipt.transaction || !receipt.items) return 0;
    
    const baseTotal = parseFloat(receipt.transaction.total_value || 0) * receipt.items.length;
    const additionalTotal = receipt.items.reduce((sum, item) => sum + parseFloat(item.additional_value || 0), 0);
    
    return baseTotal + additionalTotal;
  };
  
  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD' 
    }).format(value);
  };
  
  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        {t('billingExecutions')}
      </Typography>
      
      {/* Alert messages */}
      <Snackbar 
        open={!!success} 
        autoHideDuration={6000} 
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
      
      <Snackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
      
      {/* Add Execution button */}
      <Box sx={{ mb: 2, textAlign: direction === 'ltr' ? 'right' : 'left' }}>
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<AddIcon />}
          component={RouterLink}
          to="/billing-execution"
        >
          {t('createNewExecution')}
        </Button>
      </Box>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : receipts.length === 0 ? (
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body1">
            {t('noExecutionsFound')}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('id')}</TableCell>
                <TableCell>{t('transactionName')}</TableCell>
                <TableCell>{t('faculty')}</TableCell>
                <TableCell>{t('creationDate')}</TableCell>
                <TableCell align="right">{t('studentsCount')}</TableCell>
                <TableCell align="right">{t('totalAmount')}</TableCell>
                <TableCell align="center">{t('status')}</TableCell>
                <TableCell align="center">{t('actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {receipts.map((receipt) => (
                <TableRow key={receipt.id}>
                  <TableCell>{receipt.id}</TableCell>
                  <TableCell>{receipt.transaction?.name}</TableCell>
                  <TableCell>
                    {receipt.transaction?.faculty ? receipt.transaction.faculty[`name_${lang}`] : t('unknown')}
                  </TableCell>
                  <TableCell>{formatDate(receipt.created_at)}</TableCell>
                  <TableCell align="right">{countItems(receipt)}</TableCell>
                  <TableCell align="right">{formatCurrency(calculateTotal(receipt))}</TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={t(receipt.status)}
                      color={getStatusColor(receipt.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={t('view')}>
                      <IconButton
                        color="primary"
                        component={RouterLink}
                        to={`/billing-receipts/preview/${receipt.id}`}
                      >
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('downloadStudentsExcel')}>
                      <IconButton
                        color="info"
                        onClick={() => handleDownloadExcel(receipt.id)}
                      >
                        <DownloadIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('downloadDistributionExcel')}>
                      <IconButton
                        color="success"
                        onClick={() => handleDownloadDistributionExcel(receipt.id)}
                      >
                        <ReportIcon />
                      </IconButton>
                    </Tooltip>
                    {isAdmin() && (
                      <Tooltip title={t('delete')}>
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteClick(receipt)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>{t('confirmDelete')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('deleteExecutionConfirmation', { 
              id: receiptToDelete?.id 
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>{t('cancel')}</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default BillingExecutionsList;